import React, { useEffect, useState, useRef, useCallback } from 'react'
import Dropzone from 'react-dropzone'
import { Input, Label, Button, Card, CardBody, Col, Row, Modal, ModalHeader, ModalBody, ModalFooter, UncontrolledTooltip } from 'reactstrap';
import "../../assets/css/--component-agent.css"
import { getSingleAgent, getWebhookConfiguration, updateAgent } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { useNavigate, useParams } from 'react-router-dom'
import { formatBytes } from '../../common';
import dropzoneIcon from "../../assets/images/dropzone-icon.png"
import warningImg from "../../assets/images/warningImg.png"
import Image from '../../components/Image'
import ChatComponent from '../../components/ChatComponent';
import TestAgentComponent from './TestAgentComponent';

// Create a global store for unsaved changes
const unsavedChangesStore = {
    hasUnsavedChanges: false,
    showModal: null,
    pendingNavigation: null,
    originalNavigate: null
};

const ColorPickerField = ({ id, label, value, fallbackValue = "#ffffff", error, onChange }) => {
    const selectedColor = value || fallbackValue;

    return (
        <Col md={6} className='mb-4'>
            <Label htmlFor={id} className="text-left text-black font-bold">{label}</Label>

            <label htmlFor={id} className="widget-color-picker">
                <span
                    className="widget-color-picker__swatch"
                    style={{ backgroundColor: selectedColor }}
                />
                <span className="widget-color-picker__value">
                    {selectedColor.toUpperCase()}
                </span>
                <span className="widget-color-picker__action">Change</span>

                <input
                    type="color"
                    id={id}
                    value={selectedColor}
                    onChange={onChange}
                    className="widget-color-picker__input"
                />
            </label>

            {error && <p className="invalid-feedback">{error}</p>}
        </Col>
    );
};


const UpdateAgent = () => {
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("general");
    const [agent, setAgent] = useState({
        _id: "",
        agentId: "",
        firstMessage: "",
        language: "",
        name: "",
        promptId: "",
        purpose: "",
        type: "",
        voiceId: "",
        public_owner_id: "",
        voice_name: "",
        prompt: "",
        isPrimary: false,
        sendConversationHistory: false,
        endConversationAfterSilence: -1,
        voicemailDetection: false,
        maxConversationDuration: 0,
        keypadTouchTone: false,
        llm: "",
        initiateCallWebhookId: "",
        postCallWebhookId: "",
        placement: "bottom-right",
        inheritFromWeb: false,
        backgroundColor: "",
        textColor: "",
        chatTextColor: "",
        headerColor: "",
        buttonColor: "",
        borderRadius: ""
    });
    const [preConfigurations, setPreConfigurations] = useState([]);
    const [postConfigurations, setPostConfigurations] = useState([]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showChatAgent, setShowChatAgent] = useState(false);

    // Track initial state for comparison
    const initialAgentRef = useRef(null);
    const initialExistingFilesRef = useRef([]);

    // SIMPLIFIED: Check if there are unsaved changes
    const checkUnsavedChanges = useCallback(() => {
        if (!initialAgentRef.current) return false;

        // Check agent changes
        const agentChanged =
            agent.name !== initialAgentRef.current.name ||
            agent.type !== initialAgentRef.current.type ||
            agent.firstMessage !== initialAgentRef.current.firstMessage ||
            agent.prompt !== initialAgentRef.current.prompt ||
            agent.purpose !== initialAgentRef.current.purpose ||
            agent.isPrimary !== initialAgentRef.current.isPrimary ||
            agent.language !== initialAgentRef.current.language ||
            agent.endConversationAfterSilence !== initialAgentRef.current.endConversationAfterSilence ||
            agent.voicemailDetection !== initialAgentRef.current.voicemailDetection ||
            agent.maxConversationDuration !== initialAgentRef.current.maxConversationDuration ||
            agent.keypadTouchTone !== initialAgentRef.current.keypadTouchTone ||
            agent.llm !== initialAgentRef.current.llm ||
            agent.initiateCallWebhookId !== initialAgentRef.current.initiateCallWebhookId ||
            agent.postCallWebhookId !== initialAgentRef.current.postCallWebhookId;

        // Check existing files changes (if any were removed)
        const filesChanged = existingFiles.length !== initialExistingFilesRef.current.length;

        // Check if new files were added
        const newFilesAdded = newFiles.length > 0;

        const hasChanges = agentChanged || filesChanged || newFilesAdded;
        return hasChanges;
    }, [agent, existingFiles, newFiles]);

    // Update the global store when changes occur
    useEffect(() => {
        const hasChanges = checkUnsavedChanges();
        unsavedChangesStore.hasUnsavedChanges = hasChanges;
        unsavedChangesStore.showModal = setShowConfirmModal;
        unsavedChangesStore.originalNavigate = navigate;
    }, [checkUnsavedChanges, navigate]);

    // SIMPLE SOLUTION: Handle all navigation in one place
    useEffect(() => {
        // Store the original navigate function
        if (!unsavedChangesStore.originalNavigate) {
            unsavedChangesStore.originalNavigate = navigate;
        }

        // Create a custom navigation handler
        const customNavigate = (to, options) => {
            if (unsavedChangesStore.hasUnsavedChanges) {
                console.log('Blocking navigation to:', to);
                unsavedChangesStore.pendingNavigation = { to, options };
                setShowConfirmModal(true);
                return; // Don't navigate yet
            }
            return unsavedChangesStore.originalNavigate(to, options);
        };

        // Override the navigate function for this component
        const handleNavigation = customNavigate;

        // Listen for browser back/forward
        const handlePopState = (event) => {
            if (unsavedChangesStore.hasUnsavedChanges) {
                console.log('Blocking popstate (back button)');
                unsavedChangesStore.pendingNavigation = { to: -1 };
                setShowConfirmModal(true);
                // Re-push state to prevent actual back navigation
                window.history.pushState(null, '', window.location.pathname);
            }
        };

        window.addEventListener('popstate', handlePopState);

        // Intercept React Router Link clicks
        const interceptLinks = () => {
            // Find all React Router Links in the document
            const links = document.querySelectorAll('a[href^="/"]');

            links.forEach(link => {
                if (!link.dataset.unsavedInterceptor) {
                    link.dataset.unsavedInterceptor = 'true';

                    const originalClick = link.onclick;

                    link.onclick = (event) => {
                        if (unsavedChangesStore.hasUnsavedChanges) {
                            const href = link.getAttribute('href');
                            console.log('Blocking link click to:', href);

                            event.preventDefault();
                            event.stopPropagation();

                            unsavedChangesStore.pendingNavigation = { to: href };
                            setShowConfirmModal(true);
                            return false;
                        }

                        if (originalClick) {
                            return originalClick.call(link, event);
                        }
                    };
                }
            });
        };

        // Run interceptor periodically to catch dynamically added links
        const intervalId = setInterval(interceptLinks, 1000);
        interceptLinks(); // Run immediately

        // Cleanup
        return () => {
            window.removeEventListener('popstate', handlePopState);
            clearInterval(intervalId);

            // Clean up listeners
            document.querySelectorAll('a[href^="/"][data-unsaved-interceptor="true"]').forEach(link => {
                link.onclick = null;
                delete link.dataset.unsavedInterceptor;
            });
        };
    }, []);

    // Handle browser tab/window close
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (unsavedChangesStore.hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes on this page. Are you sure you want to leave?';
                return event.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    useEffect(() => {
        window.history.pushState({ initial: true }, '', window.location.href);
    }, []);

    // Handle navigation after confirmation
    const handleNavigationConfirm = () => {
        console.log('Confirming navigation');

        // Reset the flag
        unsavedChangesStore.hasUnsavedChanges = false;
        setShowConfirmModal(false);

        // Execute the pending navigation
        if (unsavedChangesStore.pendingNavigation) {
            const { to, options } = unsavedChangesStore.pendingNavigation;

            if (to === -1) {
                // Browser back
                window.history.back();
            } else if (unsavedChangesStore.originalNavigate) {
                // React Router navigation
                unsavedChangesStore.originalNavigate(to, options);
            }

            // Clear pending navigation
            unsavedChangesStore.pendingNavigation = null;
        }
    };

    const handleNavigationCancel = () => {
        console.log('Cancelling navigation');
        setShowConfirmModal(false);
        unsavedChangesStore.pendingNavigation = null;
    };

    // Reset store when component unmounts
    useEffect(() => {
        return () => {
            unsavedChangesStore.hasUnsavedChanges = false;
            unsavedChangesStore.pendingNavigation = null;
        };
    }, []);

    useEffect(() => {
        fetchAgent();
    }, [id]);
    useEffect(() => {
        fetchConfigurations();
    }, []);

    const fetchAgent = () => {
        setLoading(true);
        getSingleAgent(id).then((data) => {
            if (data.data) {
                const agentData = {
                    _id: data.data._id,
                    agentId: data.data.agentId,
                    name: data.data.name,
                    language: data.data.voiceObj?.language || "",
                    purpose: data.data.purpose || "",
                    prompt: data.data.prompt || "",
                    firstMessage: data.data.firstMessage || "",
                    type: data.data.type,
                    isPrimary: data.data.isPrimary || false,
                    sendConversationHistory: data.data.sendConversationHistory || false,
                    endConversationAfterSilence: data.data.endConversationAfterSilence || 60,
                    voicemailDetection: data.data.voicemailDetection || false,
                    maxConversationDuration: data.data.maxConversationDuration / 60 || 120,
                    keypadTouchTone: data.data.keypadTouchTone || false,
                    llm: data.data.llm || "gemini-2.5-flash",
                    initiateCallWebhookId: data.data.initiateCallWebhookId || "",
                    postCallWebhookId: data.data.postCallWebhookId || "",
                    placement: data.data?.placement || "",
                    // inheritFromWeb: Boolean(data.data?.inheritFromWeb) || false,
                    inheritFromWeb: false,
                    backgroundColor: data.data?.backgroundColor || "",
                    textColor: data.data?.textColor || "",
                    chatTextColor: data.data?.chatTextColor || "",
                    headerColor: data.data?.headerColor || "",
                    buttonColor: data.data?.buttonColor || "",
                    borderRadius: data.data?.borderRadius || "",
                };

                setAgent(agentData);
                // Store initial state
                initialAgentRef.current = { ...agentData };

                const files = data.data.knowledgeBase || [];
                setExistingFiles(files);
                initialExistingFilesRef.current = [...files];
            } else {
                showToast("error", "Request Failed", data.message || "Failed to fetch agent");
            }
            setLoading(false);
        }).catch((err) => {
            showToast("error", "Request Failed", err.message);
            setLoading(false);
        });
    };

    const fetchConfigurations = async () => {
        setLoading(true);
        try {
            const data = await getWebhookConfiguration();
            if (data?.status && data.data) {
                const configs = Array.isArray(data.data) ? data.data : [data.data];

                // Filter PRE and POST configurations
                const preConfigs = configs
                    .filter(config => config.webhookType === "PRE")
                    .map(config => ({
                        ...config,
                    }));

                const postConfigs = configs
                    .filter(config => config.webhookType === "POST")
                    .map(config => ({
                        ...config,
                    }));

                setPreConfigurations(preConfigs.length ? preConfigs : []);
                setPostConfigurations(postConfigs.length ? postConfigs : []);

            } else {
                setPreConfigurations([]);
                setPostConfigurations([]);
            }
        } catch (err) {
            console.error("Fetch failed:", err);
            showToast("error", "Request Failed", err.message);
            setPreConfigurations([]);
            setPostConfigurations([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        let flag = false;
        let error = { name: false, maxConversationDuration: false };

        if (!agent.name || agent.name.trim().length < 1) {
            flag = true;
            error.name = "Please provide valid agent name";
        }
        if (!agent.maxConversationDuration || agent.maxConversationDuration < 1 || agent.maxConversationDuration > 120) {
            flag = true;
            error.maxConversationDuration = "Please provide max conversation duration between 1 to 120 minutes";
        }
        if (!agent.endConversationAfterSilence || (agent.endConversationAfterSilence != -1 && agent.endConversationAfterSilence < 10)) {
            flag = true;
            error.endConversationAfterSilence = "Value should not be less than 10 seconds";
        }
        if (agent.type === "webhook") {
            if (!agent.initiateCallWebhookId || agent.initiateCallWebhookId.replace(/\s/g, '').length < 1) {
                flag = true;
                error.initiateCallWebhookId = "Please provide a valid Initiate Call Webhook";
            }
            // if (!agent.postCallWebhookId || agent.postCallWebhookId.replace(/\s/g, '').length < 1) {
            //     flag = true;
            //     error.postCallWebhookId = "Please provide a valid Post Call Webhook";
            // }
        }
        if (agent.type === "website") {
            if (!agent.inheritFromWeb) {
                if (!agent.backgroundColor || agent.backgroundColor.replace(/\s/g, '').length < 1) {
                    flag = true;
                    error.backgroundColor = "Please provide a valid background color for widget";
                }
                if (!agent.textColor || agent.textColor.replace(/\s/g, '').length < 1) {
                    flag = true;
                    error.textColor = "Please provide a valid text color for widget";
                }
                if (!agent.chatTextColor || agent.chatTextColor.replace(/\s/g, '').length < 1) {
                    flag = true;
                    error.chatTextColor = "Please provide a valid chat box color for widget";
                }
                if (!agent.headerColor || agent.headerColor.replace(/\s/g, '').length < 1) {
                    flag = true;
                    error.headerColor = "Please provide a valid header color for widget";
                }
                if (!agent.buttonColor || agent.buttonColor.replace(/\s/g, '').length < 1) {
                    flag = true;
                    error.buttonColor = "Please provide a valid button color for widget";
                }
                if (!agent.borderRadius || agent.borderRadius.replace(/\s/g, '').length < 1) {
                    flag = true;
                    error.borderRadius = "Please provide a valid border radius for widget";
                }
            }
        }

        setErrors(error);

        if (!flag) {
            setLoading(true);
            const allFiles = [...existingFiles, ...newFiles];
            const formData = new FormData();

            formData.append("_id", agent._id);
            formData.append("agentId", agent.agentId);
            formData.append("name", agent.name.trim());
            formData.append("isPrimary", agent.isPrimary);
            formData.append("sendConversationHistory", agent.sendConversationHistory);
            formData.append("prompt", agent.prompt.trim());
            formData.append("firstMessage", agent.firstMessage.trim());
            formData.append("endConversationAfterSilence", Number(agent.endConversationAfterSilence));
            formData.append("voicemailDetection", agent.voicemailDetection);
            formData.append("maxConversationDuration", Number(agent.maxConversationDuration) * 60);
            formData.append("keypadTouchTone", agent.keypadTouchTone);
            formData.append("type", agent.type);
            formData.append("llm", agent.llm);
            if (agent.type === "webhook") {
                formData.append("initiateCallWebhookId", agent.initiateCallWebhookId);
                formData.append("postCallWebhookId", agent.postCallWebhookId);
            }
            if (agent.type === "website") {
                formData.append("placement", agent.placement);
                formData.append("inheritFromWeb", agent.inheritFromWeb);
                if (!agent.inheritFromWeb) {
                    formData.append("backgroundColor", agent.backgroundColor);
                    formData.append("textColor", agent.textColor);
                    formData.append("chatTextColor", agent.chatTextColor);
                    formData.append("headerColor", agent.headerColor);
                    formData.append("buttonColor", agent.buttonColor);
                    formData.append("borderRadius", agent.borderRadius);
                }
            }

            for (let file of newFiles) {
                formData.append("knowledgeBaseFiles", file);
            }

            formData.append("knowledgeBase", JSON.stringify(existingFiles));
            formData.append("language", agent.language);
            formData.append("purpose", agent.purpose.trim());

            updateAgent(formData).then((data) => {
                if (data.data) {
                    showToast("success", "Agent Updated", "Your agent has been updated successfully.");

                    // Update initial state after successful save
                    initialAgentRef.current = { ...agent };
                    initialExistingFilesRef.current = [...existingFiles];
                    unsavedChangesStore.hasUnsavedChanges = false;

                    // Clear new files
                    setNewFiles([]);

                    // Clear any pending navigation
                    unsavedChangesStore.pendingNavigation = null;

                    setLoading(false);
                    navigate('/agents');
                }
            }).catch((e) => {
                showToast("error", "Update Failed", "Your agent could not be updated.");
                setLoading(false);
            });
        }
    };

    const handleAcceptedFiles = (acceptedFiles) => {
        setErrors((prev) => ({ ...prev, file: '' }));

        const allowedFormats = ['pdf', 'txt', 'docx', 'epub', 'html'];
        const maxFileSize = 22020096;

        const validFormatFiles = acceptedFiles.filter((file) => {
            const fileExt = file.name.split('.').pop().toLowerCase();
            return allowedFormats.includes(fileExt);
        });

        if (validFormatFiles.length !== acceptedFiles.length) {
            setErrors((prev) => ({
                ...prev,
                file: 'Only upload files with allowed formats (pdf, txt, docx, epub, html)'
            }));
            return;
        }

        const validSizeFiles = validFormatFiles.filter((file) => {
            return file.size <= maxFileSize && file.size > 0;
        });

        if (validSizeFiles.length !== validFormatFiles.length) {
            if (validFormatFiles.some((file) => file.size === 0)) {
                setErrors((prev) => ({ ...prev, file: 'File cannot be empty' }));
            } else {
                setErrors((prev) => ({ ...prev, file: 'File size should not be greater than 21 MB' }));
            }
            return;
        }

        const formattedFiles = validSizeFiles.map(file =>
            Object.assign(file, { formattedSize: formatBytes(file.size) })
        );

        setNewFiles((prev) => [...prev, ...formattedFiles]);
    };

    const clearExistingFile = (id) => {
        setExistingFiles(prev => prev.filter((file) => file._id !== id));
    };

    const clearNewFile = (index) => {
        setNewFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Handle agent field changes
    const handleAgentChange = (field, value) => {
        setAgent(prev => ({ ...prev, [field]: value }));
    };

    const copyEmbedCode = (embedCode) => {
        try {
            // Copy to clipboard
            navigator.clipboard.writeText(embedCode).then(() => {
                showToast("success", "Copied!", "Embed code copied to clipboard");
            }).catch(() => {
                // Fallback for older browsers
                const textarea = document.createElement('textarea');
                textarea.value = embedCode;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                showToast("success", "Copied!", "Embed code copied to clipboard");
            });
        } catch (error) {
            console.error('Error copying code:', error);
            showToast("error", "Copy Failed", "Could not copy embed code.");
        }
    };

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    {/* ReactStrap Modal for Unsaved Changes */}
                    <Modal
                        isOpen={showConfirmModal}
                        toggle={handleNavigationCancel}
                        centered
                        backdrop="static"
                        keyboard={false}
                        className="unsaved-changes-modal"
                        style={{ zIndex: 9999 }}
                    >
                        {/* <ModalHeader toggle={handleNavigationCancel}>
                            <i className="ri-alert-fill me-2"></i>
                            Unsaved Changes
                        </ModalHeader> */}
                        <ModalBody style={{ padding: '20px' }}>
                            <Row>
                                <Col md={12} className='text-center'>
                                    <Image src={warningImg} alt="Event" className='rounded-circle mb-3' height='150px' width='150px' objectFit='cover' />
                                    <h2>Unsaved Changes</h2>
                                    <p> If you leave this page, all unsaved changes will be lost. Are you sure you want to leave?</p>
                                </Col>
                            </Row>
                        </ModalBody>
                        <ModalFooter style={{
                            borderTop: '1px solid #dee2e6',
                            padding: '15px 20px',
                            margin: '0 auto'
                        }}>
                            <button
                                onClick={handleNavigationConfirm}
                                className="px-2 py-2 rounded outline-gradient-btn fs-16"
                            >
                                <span><i className="ri-logout-box-line me-1"></i> Leave Anyway</span>

                            </button>
                            <button
                                onClick={handleNavigationCancel}
                                className="font-normal py-2 px-4 rounded fs-16 text-decoration-none text-white linear-gradient-btn"
                            >
                                <i className="ri-arrow-go-back-line me-1"></i> Stay on Page
                            </button>
                        </ModalFooter>
                    </Modal>

                    <div className="py-3 row">
                        <div className="col-12">
                            <Row className="my-2 align-items-center">
                                <Col md={6}>
                                    <h4>Edit Agent</h4>
                                    <p className='text-muted fs-12'>Edit the agent details</p>
                                </Col>
                                <Col md={6} className='text-right'>
                                    <Button
                                        disabled={loading}
                                        onClick={handleSubmit}
                                        className="px-4 py-2 rounded linear-gradient-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Please Wait
                                            </>
                                        ) : "Update"}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row>
                        <Col md={12}>
                            <div className="d-flex justify-content-start mb-4">
                                <div className="bg-light rounded-3 p-1 d-inline-flex">
                                    <Button
                                        color={`${mode === 'general' ? 'primary' : 'link'}`}
                                        size="lg"
                                        className={`rounded-3 px-4 py-2 fs-16 fw-400 ${mode === 'general' ? 'linear-gradient-btn' : 'text-muted'}`}
                                        onClick={() => setMode('general')}
                                    >
                                        General Settings
                                    </Button>
                                    {
                                        agent.type !== "website" &&
                                        <Button
                                            color={`${mode === 'test' ? 'primary' : 'link'}`}
                                            size="lg"
                                            className={`rounded-3 px-4 py-2 fs-16 fw-400 ${mode === 'test' ? 'linear-gradient-btn' : 'text-muted'}`}
                                            onClick={() => setMode('test')}
                                        >
                                            Test Agent
                                        </Button>
                                    }
                                    <Button
                                        color={`${mode === 'advance' ? 'primary' : 'link'}`}
                                        size="lg"
                                        className={`rounded-3 px-4 py-2 fs-16 fw-400 ${mode === 'advance' ? 'linear-gradient-btn' : 'text-muted'}`}
                                        onClick={() => setMode('advance')}
                                    >
                                        Advanced Settings
                                    </Button>
                                    {
                                        agent.type === "webhook" &&
                                        <Button
                                            color={`${mode === 'webhook' ? 'primary' : 'link'}`}
                                            size="lg"
                                            className={`rounded-3 px-4 py-2 fs-16 fw-400 ${mode === 'webhook' ? 'linear-gradient-btn' : 'text-muted'}`}
                                            onClick={() => setMode('webhook')}
                                        >
                                            Webhook Settings
                                        </Button>
                                    }
                                    {
                                        agent.type === "website" &&
                                        <Button
                                            color={`${mode === 'website' ? 'primary' : 'link'}`}
                                            size="lg"
                                            className={`rounded-3 px-4 py-2 fs-16 fw-400 ${mode === 'website' ? 'linear-gradient-btn' : 'text-muted'}`}
                                            onClick={() => setMode('website')}
                                        >
                                            Web Agent Settings
                                        </Button>
                                    }
                                </div>
                            </div>
                            {
                                mode === "general" ?
                                    <Card className='bg-white rounded-2'>
                                        <CardBody>
                                            <Row>
                                                <Col md={6} className='mb-4'>
                                                    <Label htmlFor="agentName" className="text-left text-black font-bold">
                                                        Agent Name
                                                    </Label>
                                                    <input
                                                        type="text"
                                                        id="agentName"
                                                        className={`form-control custom-input mb-3 ${errors.name ? 'is-invalid' : ''}`}
                                                        value={agent.name}
                                                        onChange={(e) => handleAgentChange('name', e.target.value)}
                                                    />
                                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}

                                                    <Row>
                                                        <Col>
                                                            <div className='mt-3'>
                                                                <Label className="text-left text-black font-bold">
                                                                    Send Conversation History
                                                                </Label>
                                                                <div className="form-check form-switch gradient-switch agent-update-switch">
                                                                    <input
                                                                        role='switch'
                                                                        type="checkbox"
                                                                        checked={agent.sendConversationHistory}
                                                                        onChange={() => handleAgentChange('sendConversationHistory', !agent.sendConversationHistory)}
                                                                        className="form-check-input"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col>
                                                            {agent.type === "inbound" && (
                                                                <div className='mt-3'>
                                                                    <Label className="text-left text-black font-bold">
                                                                        Primary Inbound Agent
                                                                    </Label>
                                                                    <div className="form-check form-switch gradient-switch agent-update-switch">
                                                                        <input
                                                                            role='switch'
                                                                            type="checkbox"
                                                                            checked={agent.isPrimary}
                                                                            onChange={() => handleAgentChange('isPrimary', !agent.isPrimary)}
                                                                            className="form-check-input"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                        </Col>
                                                    </Row>
                                                </Col>

                                                <Col md={6} className='mb-4'>
                                                    <Label htmlFor="language" className="text-left text-black font-bold">
                                                        Choose Language
                                                    </Label>
                                                    <Input
                                                        id="language"
                                                        className='custom-select rounded border-0 py-2'
                                                        type="select"
                                                        disabled={true}
                                                        value={agent.language}
                                                        onChange={(e) => handleAgentChange('language', e.target.value)}
                                                    >
                                                        <option value="ar">Arabic</option>
                                                        <option value="bg">Bulgarian</option>
                                                        <option value="zh">Chinese</option>
                                                        <option value="hr">Croatian</option>
                                                        <option value="cs">Czech</option>
                                                        <option value="da">Danish</option>
                                                        <option value="nl">Dutch</option>
                                                        <option value="en">English</option>
                                                        <option value="fil">Filipino</option>
                                                        <option value="fi">Finnish</option>
                                                        <option value="fr">French</option>
                                                        <option value="de">German</option>
                                                        <option value="el">Greek</option>
                                                        <option value="hi">Hindi</option>
                                                        <option value="hu">Hungarian</option>
                                                        <option value="id">Indonesian</option>
                                                        <option value="it">Italian</option>
                                                        <option value="ja">Japanese</option>
                                                        <option value="ko">Korean</option>
                                                        <option value="ms">Malay</option>
                                                        <option value="no">Norwegian</option>
                                                        <option value="pl">Polish</option>
                                                        <option value="pt">Portuguese</option>
                                                        <option value="ro">Romanian</option>
                                                        <option value="ru">Russian</option>
                                                        <option value="sk">Slovak</option>
                                                        <option value="es">Spanish</option>
                                                        <option value="sw">Swahili</option>
                                                        <option value="sv">Swedish</option>
                                                        <option value="ta">Tamil</option>
                                                        <option value="tr">Turkish</option>
                                                        <option value="uk">Ukrainian</option>
                                                        <option value="vi">Vietnamese</option>
                                                    </Input>
                                                </Col>

                                                <Col md={6} className='mb-4 agents-knowledgebase py-0 px-2'>
                                                    <Row>
                                                        <Col md={12} className='mb-4'>
                                                            <Label htmlFor="agentType" className="text-left text-black font-bold"> Agent Type </Label>
                                                            <Input id="agentType" className='custom-select rounded border-0 py-2' type="select" value={agent.type} onChange={(e) => { setAgent((prev) => { return { ...prev, type: e.target.value } }); }} >
                                                                <option value="outbound">Outbound</option>
                                                                <option value="inbound">Inbound</option>
                                                                <option value="webhook">Webhook</option>
                                                                <option value="website">Website</option>
                                                            </Input>
                                                        </Col>
                                                        <Col md={12}>
                                                            <Label htmlFor="purpose" className="text-left text-black font-bold">
                                                                Agent Knowledgebase
                                                            </Label>
                                                            <div className='custom-file-upload'>
                                                                <Dropzone
                                                                    accept={{
                                                                        'application/epub+zip': ['.epub'],
                                                                        'application/pdf': ['.pdf'],
                                                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                                                        'text/plain': ['.txt'],
                                                                        'text/html': ['.html', '.htm']
                                                                    }}
                                                                    multiple={true}
                                                                    onDropAccepted={handleAcceptedFiles}
                                                                    onDropRejected={() => {
                                                                        setErrors((prev) => ({
                                                                            ...prev,
                                                                            file: "Only upload files with allowed formats (pdf, txt, docx, epub, html)"
                                                                        }));
                                                                    }}
                                                                >
                                                                    {({ getRootProps, getInputProps }) => (
                                                                        <div className="dropzone" {...getRootProps()}>
                                                                            <input {...getInputProps()} />
                                                                            <div className="dz-message">
                                                                                <img src={dropzoneIcon} alt="dropzone icon" className='my-2' />
                                                                                <h6>Drag & Drop to upload file</h6>
                                                                                <p>OR</p>
                                                                                <span>Browse File</span>
                                                                                <p>(Max: 21MB | Supports: pdf, txt, docx, html, epub)</p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Dropzone>

                                                                {existingFiles.length > 0 && (
                                                                    <div className="uploaded-files">
                                                                        {existingFiles.map((file, index) => (
                                                                            <div key={file._id || `existing-${index}`} className="uploaded-file-card">
                                                                                <div className="file-info">
                                                                                    <p>
                                                                                        <i className="ri-file-text-line me-2 fs-20"></i>
                                                                                        <b>{file._id ? file.name : file.path}</b>
                                                                                    </p>
                                                                                </div>
                                                                                <span
                                                                                    className="clear-btn"
                                                                                    onClick={() => file._id ? clearExistingFile(file._id) : clearNewFile(index)}
                                                                                >
                                                                                    <i className="ri-close-circle-line"></i>
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {newFiles.length > 0 && (
                                                                    <div className="uploaded-files">
                                                                        {newFiles.map((file, index) => (
                                                                            <div key={`new-${index}`} className="uploaded-file-card">
                                                                                <div className="file-info">
                                                                                    <p>
                                                                                        <i className="ri-file-text-line me-2 fs-20"></i>
                                                                                        <b>{file.name}</b>
                                                                                    </p>
                                                                                </div>
                                                                                <span
                                                                                    className="clear-btn"
                                                                                    onClick={() => clearNewFile(index)}
                                                                                >
                                                                                    <i className="ri-close-circle-line"></i>
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}
                                                            </div></Col>
                                                    </Row>

                                                </Col>

                                                <Col md={6} className='mb-4'>
                                                    <Row>
                                                        <Col md={12} className='mb-4'>
                                                            <Label htmlFor="firstMessage" className="text-left text-black font-bold">
                                                                <small>First Message</small>
                                                            </Label>
                                                            <Input
                                                                disabled={false}
                                                                id="firstMessage"
                                                                className='custom-input rounded border-0 py-2'
                                                                type="text"
                                                                value={agent.firstMessage || ""}
                                                                onChange={(e) => handleAgentChange('firstMessage', e.target.value)}
                                                            />
                                                        </Col>

                                                        <Col md={12} className='mb-4'>
                                                            <Label className="text-left text-black font-bold">
                                                                <small>Prompt</small>
                                                            </Label>
                                                            <textarea
                                                                disabled={false}
                                                                value={agent.prompt || ""}
                                                                onChange={(e) => handleAgentChange('prompt', e.target.value)}
                                                                className='fs-16 rounded p-2 custom-textarea'
                                                                rows="5"
                                                            />
                                                        </Col>

                                                        <Col md={12} className='mb-1'>
                                                            <Label htmlFor="purpose" className="text-left text-black font-bold">
                                                                <small>Purpose</small>
                                                            </Label>
                                                            <input
                                                                type="text"
                                                                disabled={true}
                                                                id="purpose"
                                                                className={`form-control custom-input`}
                                                                value={agent.purpose || ""}
                                                                onChange={(e) => handleAgentChange('purpose', e.target.value)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                    :
                                    mode === "test" ?
                                        <TestAgentComponent agent={agent} />
                                        :
                                    mode === "advance" ?
                                        <Card className='bg-transparent rounded-2 shadow-none'>
                                            <CardBody>
                                                <Row>
                                                    <Col md={6}>
                                                        <Card className='bg-white rounded-4 h-100'>
                                                            <CardBody>
                                                                <h4 className='fs-22 fw-600 mt-2'>End Conversation After Silence</h4>
                                                                <p className='fs-15 mb-4 text-muted'>The maximum number of seconds since the user last spoke. If exceeded, the call will terminate. A value of -1 means there is no fixed cutoff.</p>
                                                                <Row className='mb-1'>
                                                                    <Col md={8}>
                                                                        <p className='fs-15 mb-0 text-black'>Silence Duration (seconds)</p>
                                                                    </Col>
                                                                    <Col md={4} className='text-right'>
                                                                        <div className='d-flex align-items-center justify-content-end gap-1'>
                                                                            <input
                                                                                type='text'
                                                                                id="silence"
                                                                                value={agent.endConversationAfterSilence}
                                                                                onChange={(e) => {
                                                                                    let value = e.target.value;
                                                                                    value = value.replace(/[^0-9-]/g, '');
                                                                                    if (value.includes('-') && value.indexOf('-') !== 0) {
                                                                                        value = value.replace(/-/g, '');
                                                                                    }
                                                                                    // Only allow -1 or positive numbers (max 2 chars unless it's -1)
                                                                                    if ((value === '-' || value === '-1') || (value.length <= 2 && /^[0-9]+$/.test(value)) || value === "") {
                                                                                        handleAgentChange('endConversationAfterSilence', value);
                                                                                    }
                                                                                }}
                                                                                min={-1}
                                                                                className="custom-input form-control rounded border-0 py-2 form-control w-30"
                                                                            />
                                                                            <label htmlFor="silence" className='fw-500 fs-16'>sec</label>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                {errors.endConversationAfterSilence && <div className="invalid-feedback">{errors.endConversationAfterSilence}</div>}
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Card className='bg-white rounded-4 h-100'>
                                                            <CardBody>
                                                                <h4 className='fs-22 fw-600 mt-2'>Voicemail Detection</h4>
                                                                <p className='fs-15 mb-4 text-muted'>Allows agent to detect voicemail systems and optionally leave a message.</p>
                                                                <Row className='mb-1'>
                                                                    <Col md={8}>
                                                                        <p className='fs-15 mb-0 text-black'>Enable Voicemail Detection</p>
                                                                    </Col>
                                                                    <Col md={4} className='text-right'>
                                                                        <div className="form-check form-switch gradient-switch agent-update-switch" dir="ltr">
                                                                            <input role='switch' type="checkbox" checked={agent.voicemailDetection} onChange={(e) => { handleAgentChange("voicemailDetection", e.target.checked); }} className="form-check-input float-right" />
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                    <Col md={6} className='mt-4'>
                                                        <Card className='bg-white rounded-4 h-100'>
                                                            <CardBody>
                                                                <h4 className='fs-22 fw-600 mt-2'>Max Conversation Duration</h4>
                                                                <p className='fs-15 mb-4 text-muted'>The maximum number of seconds that a conversation can last.</p>
                                                                <Row className='mb-1'>
                                                                    <Col md={8}>
                                                                        <p className='fs-15 mb-0 text-black'>Maximum Call Duration</p>
                                                                    </Col>
                                                                    <Col md={4} className='text-right'>
                                                                        <div className='d-flex align-items-center justify-content-end gap-1'>
                                                                            <input
                                                                                type='text'
                                                                                id="duration"
                                                                                value={agent.maxConversationDuration}
                                                                                onChange={(e) => {
                                                                                    let value = e.target.value;
                                                                                    value = value.replace(/[^0-9-]/g, '');
                                                                                    if (value.includes('-') && value.indexOf('-') !== 0) {
                                                                                        value = value.replace(/-/g, '');
                                                                                    }
                                                                                    // Only allow -1 or positive numbers (max 2 chars unless it's -1)
                                                                                    if ((value.length <= 4 && /^[0-9]+$/.test(value)) || value === "") {
                                                                                        handleAgentChange('maxConversationDuration', value);
                                                                                    }
                                                                                }}
                                                                                min={-1}
                                                                                className="custom-input form-control rounded border-0 py-2 form-control w-30"
                                                                            />
                                                                            <label htmlFor="duration" className='fw-500 fs-16'>min</label>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                {errors.maxConversationDuration && <div className="invalid-feedback">{errors.maxConversationDuration}</div>}
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                    <Col md={6} className='mt-4'>
                                                        <Card className='bg-white rounded-4 h-100'>
                                                            <CardBody>
                                                                <h4 className='fs-22 fw-600 mt-2'>Play Keypad Touch Tone</h4>
                                                                <p className='fs-15 mb-4 text-muted'>Gives agent the ability to play keypad touch tones during a phone call.</p>
                                                                <Row className='mb-1'>
                                                                    <Col md={8}>
                                                                        <p className='fs-15 mb-0 text-black'>Play Keypad Touch Tone</p>
                                                                    </Col>
                                                                    <Col md={4} className='text-right'>
                                                                        <div className="form-check form-switch gradient-switch agent-update-switch" dir="ltr">
                                                                            <input role='switch' type="checkbox" checked={agent.keypadTouchTone} onChange={(e) => { handleAgentChange("keypadTouchTone", e.target.checked); }} className="form-check-input float-right" />
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                    <Col md={6} className='mt-4'>
                                                        <Card className='bg-white rounded-4 h-100'>
                                                            <CardBody>
                                                                <h4 className='fs-22 fw-600 mt-2'>LLM</h4>
                                                                <p className='fs-15 mb-4 text-muted'>Choose LLM for your agent.</p>
                                                                <Row className='mb-1'>
                                                                    <Col md={8}>
                                                                        <p className='fs-15 mb-0 text-black'>LLM </p>
                                                                    </Col>
                                                                    <Col md={4} className='text-right'>
                                                                        <div className="form-check form-switch gradient-switch agent-update-switch" dir="ltr">
                                                                            <Input
                                                                                id="llm"
                                                                                className='custom-select rounded border-0 py-2'
                                                                                type="select"
                                                                                value={agent.llm}
                                                                                onChange={(e) => handleAgentChange('llm', e.target.value)}
                                                                            >
                                                                                <option value="gpt-4o-mini">gpt-4o-mini</option>
                                                                                <option value="gpt-4o">gpt-4o</option>
                                                                                <option value="gpt-4">gpt-4</option>
                                                                                <option value="gpt-4-turbo">gpt-4-turbo</option>
                                                                                <option value="gpt-4.1">gpt-4.1</option>
                                                                                <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                                                                                <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                                                                                <option value="gpt-5">gpt-5</option>
                                                                                <option value="gpt-5.1">gpt-5.1</option>
                                                                                <option value="gpt-5.2">gpt-5.2</option>
                                                                                <option value="gpt-5.2-chat-latest">gpt-5.2-chat-latest</option>
                                                                                <option value="gpt-5.4">gpt-5.4</option>
                                                                                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                                                                                <option value="gpt-5.4-nano">gpt-5.4-nano</option>
                                                                                <option value="gpt-5.5">gpt-5.5</option>
                                                                                <option value="gpt-5-mini">gpt-5-mini</option>
                                                                                <option value="gpt-5-nano">gpt-5-nano</option>
                                                                                <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
                                                                                <option value="gemini-1.5-pro">gemini-1.5-pro</option>
                                                                                <option value="gemini-1.5-flash">gemini-1.5-flash</option>
                                                                                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                                                                                <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
                                                                                <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</option>
                                                                                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                                                                                <option value="gemini-3-pro-preview">gemini-3-pro-preview</option>
                                                                                <option value="gemini-3-flash-preview">gemini-3-flash-preview</option>
                                                                                <option value="gemini-3.1-pro-preview">gemini-3.1-pro-preview</option>
                                                                                <option value="gemini-3.1-flash-lite-preview">gemini-3.1-flash-lite-preview</option>
                                                                                <option value="gemini-3.1-flash-lite">gemini-3.1-flash-lite</option>
                                                                                <option value="gemini-3.5-flash">gemini-3.5-flash</option>
                                                                                <option value="gemini-2.5-flash-preview-09-2025">gemini-2.5-flash-preview-09-2025</option>
                                                                                <option value="gemini-2.5-flash-lite-preview-09-2025">gemini-2.5-flash-lite-preview-09-2025</option>
                                                                                <option value="gemini-2.5-flash-preview-05-20">gemini-2.5-flash-preview-05-20</option>
                                                                                <option value="gemini-2.5-flash-preview-04-17">gemini-2.5-flash-preview-04-17</option>
                                                                                <option value="gemini-2.5-flash-lite-preview-06-17">gemini-2.5-flash-lite-preview-06-17</option>
                                                                                <option value="gemini-2.0-flash-lite-001">gemini-2.0-flash-lite-001</option>
                                                                                <option value="gemini-2.0-flash-001">gemini-2.0-flash-001</option>
                                                                                <option value="gemini-1.5-flash-002">gemini-1.5-flash-002</option>
                                                                                <option value="gemini-1.5-flash-001">gemini-1.5-flash-001</option>
                                                                                <option value="gemini-1.5-pro-002">gemini-1.5-pro-002</option>
                                                                                <option value="gemini-1.5-pro-001">gemini-1.5-pro-001</option>
                                                                                <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                                                                                <option value="claude-opus-4-7">claude-opus-4-7</option>
                                                                                <option value="claude-sonnet-4-6">claude-sonnet-4-6</option>
                                                                                <option value="claude-sonnet-4">claude-sonnet-4</option>
                                                                                <option value="claude-haiku-4-5">claude-haiku-4-5</option>
                                                                                <option value="claude-3-7-sonnet">claude-3-7-sonnet</option>
                                                                                <option value="claude-3-5-sonnet">claude-3-5-sonnet</option>
                                                                                <option value="claude-3-5-sonnet-v1">claude-3-5-sonnet-v1</option>
                                                                                <option value="claude-3-haiku">claude-3-haiku</option>
                                                                                <option value="claude-sonnet-4@20250514">claude-sonnet-4@20250514</option>
                                                                                <option value="claude-sonnet-4-5@20250929">claude-sonnet-4-5@20250929</option>
                                                                                <option value="claude-haiku-4-5@20251001">claude-haiku-4-5@20251001</option>
                                                                                <option value="claude-3-7-sonnet@20250219">claude-3-7-sonnet@20250219</option>
                                                                                <option value="claude-3-5-sonnet@20240620">claude-3-5-sonnet@20240620</option>
                                                                                <option value="claude-3-5-sonnet-v2@20241022">claude-3-5-sonnet-v2@20241022</option>
                                                                                <option value="claude-3-haiku@20240307">claude-3-haiku@20240307</option>
                                                                                <option value="grok-beta">grok-beta</option>
                                                                                <option value="custom-llm">custom-llm</option>
                                                                                <option value="qwen3-4b">qwen3-4b</option>
                                                                                <option value="qwen3-30b-a3b">qwen3-30b-a3b</option>
                                                                                <option value="qwen36-35b-a3b">qwen36-35b-a3b</option>
                                                                                <option value="qwen35-397b-a17b">qwen35-397b-a17b</option>
                                                                                <option value="gpt-oss-20b">gpt-oss-20b</option>
                                                                                <option value="gpt-oss-120b">gpt-oss-120b</option>
                                                                                <option value="glm-45-air-fp8">glm-45-air-fp8</option>
                                                                                <option value="gpt-5-2025-08-07">gpt-5-2025-08-07</option>
                                                                                <option value="gpt-5.1-2025-11-13">gpt-5.1-2025-11-13</option>
                                                                                <option value="gpt-5.2-2025-12-11">gpt-5.2-2025-12-11</option>
                                                                                <option value="gpt-5.4-2026-03-05">gpt-5.4-2026-03-05</option>
                                                                                <option value="gpt-5.4-mini-2026-03-17">gpt-5.4-mini-2026-03-17</option>
                                                                                <option value="gpt-5.4-nano-2026-03-17">gpt-5.4-nano-2026-03-17</option>
                                                                                <option value="gpt-5.5-2026-04-23">gpt-5.5-2026-04-23</option>
                                                                                <option value="gpt-5-mini-2025-08-07">gpt-5-mini-2025-08-07</option>
                                                                                <option value="gpt-5-nano-2025-08-07">gpt-5-nano-2025-08-07</option>
                                                                                <option value="gpt-4.1-2025-04-14">gpt-4.1-2025-04-14</option>
                                                                                <option value="gpt-4.1-mini-2025-04-14">gpt-4.1-mini-2025-04-14</option>
                                                                                <option value="gpt-4.1-nano-2025-04-14">gpt-4.1-nano-2025-04-14</option>
                                                                                <option value="gpt-4o-mini-2024-07-18">gpt-4o-mini-2024-07-18</option>
                                                                                <option value="gpt-4o-2024-11-20">gpt-4o-2024-11-20</option>
                                                                                <option value="gpt-4o-2024-08-06">gpt-4o-2024-08-06</option>
                                                                                <option value="gpt-4o-2024-05-13">gpt-4o-2024-05-13</option>
                                                                                <option value="gpt-4-0613">gpt-4-0613</option>
                                                                                <option value="gpt-4-0314">gpt-4-0314</option>
                                                                                <option value="gpt-4-turbo-2024-04-09">gpt-4-turbo-2024-04-09</option>
                                                                                <option value="gpt-3.5-turbo-0125">gpt-3.5-turbo-0125</option>
                                                                                <option value="gpt-3.5-turbo-1106">gpt-3.5-turbo-1106</option>
                                                                                <option value="watt-tool-8b">watt-tool-8b</option>
                                                                                <option value="watt-tool-70b">watt-tool-70b</option>
                                                                            </Input>
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                        :
                                        mode === "webhook" ?
                                            <Card className='bg-white rounded-3 shadow-none py-4'>
                                                <CardBody>
                                                    <Row>
                                                        <Col md={6} className='mb-4'>
                                                            <Label htmlFor="agentType" className="text-left text-black font-bold"> Initial Call Webhook </Label>
                                                            <Input id="agentType" className='custom-select rounded border-0 py-2' type="select" value={agent.initiateCallWebhookId} onChange={(e) => { setAgent((prev) => { return { ...prev, initiateCallWebhookId: e.target.value } }); }} >
                                                                <option value="">Select Option</option>
                                                                {
                                                                    preConfigurations.map((config, i) => {
                                                                        return <option key={config._id} value={config._id}>{config.title}</option>
                                                                    })
                                                                }
                                                            </Input>
                                                            {errors.initiateCallWebhookId && <p className="invalid-feedback">{errors.initiateCallWebhookId}</p>}
                                                        </Col>
                                                        <Col md={6} className='mb-4'>
                                                            <Label htmlFor="agentType" className="text-left text-black font-bold"> Post Call Webhook </Label>
                                                            <Input id="agentType" className='custom-select rounded border-0 py-2' type="select" value={agent.postCallWebhookId} onChange={(e) => { setAgent((prev) => { return { ...prev, postCallWebhookId: e.target.value } }); }} >
                                                                <option value="">Select Option</option>
                                                                {
                                                                    postConfigurations.map((config, i) => {
                                                                        return <option key={config._id} value={config._id}>{config.title}</option>
                                                                    })
                                                                }
                                                            </Input>
                                                            {errors.postCallWebhookId && <p className="invalid-feedback">{errors.postCallWebhookId}</p>}
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                            :
                                            <Card className='bg-white rounded-3 shadow-none py-4'>
                                                <CardBody>
                                                    <Row>
                                                        {/* <Col md={6} className='mb-4'>
                                                            <Row>
                                                                <Col md={6}>
                                                                    <div className='mt-3'>
                                                                        <Label className="text-left text-black font-bold">
                                                                            Inherit Styles From Website
                                                                        </Label>
                                                                        <div className="form-check form-switch gradient-switch agent-update-switch">
                                                                            <input
                                                                                role='switch'
                                                                                type="checkbox"
                                                                                checked={agent.inheritFromWeb}
                                                                                onChange={(e) => setAgent((prev) => { return { ...prev, inheritFromWeb: !agent.inheritFromWeb } })}
                                                                                className="form-check-input"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Col> */}
                                                        <Col md={6} className='mb-4'>
                                                            <Label htmlFor="placement" className="text-left text-black font-bold"> Placement </Label>
                                                            <Input id="placement" className='custom-select rounded border-0 py-2' type="select" value={agent.placement} onChange={(e) => { setAgent((prev) => { return { ...prev, placement: e.target.value } }); }} >
                                                                {/* <option value="bottom">Bottom</option> */}
                                                                <option value="bottom-right">Bottom-Right</option>
                                                                <option value="bottom-left">Bottom-Left</option>
                                                                {/* <option value="top">Top</option> */}
                                                                <option value="top-right">Top-Right</option>
                                                                <option value="top-left">Top-Left</option>
                                                            </Input>
                                                        </Col>
                                                        {
                                                            !agent.inheritFromWeb ?
                                                                <>
                                                                    <ColorPickerField
                                                                        id="bg-color"
                                                                        label="Chat Background Color"
                                                                        value={agent.backgroundColor}
                                                                        fallbackValue="#ffffff"
                                                                        error={errors.backgroundColor}
                                                                        onChange={(e) => setAgent(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                                                    />

                                                                    <ColorPickerField
                                                                        id="text-color"
                                                                        label="Header Text Color"
                                                                        value={agent.textColor}
                                                                        fallbackValue="#ffffff"
                                                                        error={errors.textColor}
                                                                        onChange={(e) => setAgent(prev => ({ ...prev, textColor: e.target.value }))}
                                                                    />

                                                                    <ColorPickerField
                                                                        id="chat-color"
                                                                        label="Chat Text Color"
                                                                        value={agent.chatTextColor}
                                                                        fallbackValue="#333333"
                                                                        error={errors.chatTextColor}
                                                                        onChange={(e) => setAgent(prev => ({ ...prev, chatTextColor: e.target.value }))}
                                                                    />

                                                                    <ColorPickerField
                                                                        id="header-color"
                                                                        label="Header Background Color"
                                                                        value={agent.headerColor}
                                                                        fallbackValue="#55d472"
                                                                        error={errors.headerColor}
                                                                        onChange={(e) => setAgent(prev => ({ ...prev, headerColor: e.target.value }))}
                                                                    />

                                                                    <ColorPickerField
                                                                        id="button-color"
                                                                        label="Button Color"
                                                                        value={agent.buttonColor}
                                                                        fallbackValue="#55d472"
                                                                        error={errors.buttonColor}
                                                                        onChange={(e) => setAgent(prev => ({ ...prev, buttonColor: e.target.value }))}
                                                                    />

                                                                    <Col md={6} className='mb-4'>
                                                                        <Label htmlFor="border-radius" className="text-left text-black font-bold"> Border Radius </Label>
                                                                        <input type="text" id="border-radius" placeholder="e.g. 8px" className={`form-control custom-input`} value={agent.borderRadius} onChange={(e) => setAgent((prev) => { return { ...prev, borderRadius: e.target.value } })} />
                                                                        {errors.borderRadius && <p className="invalid-feedback">{errors.borderRadius}</p>}
                                                                    </Col>
                                                                </> : null
                                                        }
                                                        <Col md={12} className='mb-4 embed-code-section'>
                                                            <Label className="fw-bold">
                                                                Embed Code
                                                            </Label>
                                                            <div className="alert alert-info py-2 px-3 mb-2 fs-13">
                                                                <i className="ri-information-line me-1"></i>
                                                                Copy this code and paste it into your website's root HTML, preferably at the top of <code>&lt;body&gt;</code> tag. The chat widget will appear on your site automatically.
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="code-preview border rounded cursor-pointer">
                                                                    <pre className="m-0 p-1"
                                                                        onClick={() => copyEmbedCode(
                                                                            `
                                                                        <ai-assistant agent-id="${agent.agentId}" userId="${localStorage.getItem("userId")}" ${!agent.inheritFromWeb ? `backgroundColor="${agent.backgroundColor}" textColor="${agent.textColor}" chatTextColor="${agent.chatTextColor}" headerColor="${agent.headerColor}" buttonColor="${agent.buttonColor}" borderRadius="${agent.borderRadius}"` : ""}></ai-assistant>
                                                                        <script src="${process.env.REACT_APP_URL}/widgets/v1/ai-widget.js" async></script>
                                                                        `
                                                                        )}
                                                                    >
                                                                        {(() => {
                                                                            return (
                                                                                `<ai-assistant agent-id="${agent.agentId}" userId="${localStorage.getItem("userId")}" ${!agent.inheritFromWeb ? `backgroundColor="${agent.backgroundColor}" textColor="${agent.textColor}" chatTextColor="${agent.chatTextColor}" headerColor="${agent.headerColor}" buttonColor="${agent.buttonColor}" borderRadius="${agent.borderRadius}"` : ""}></ai-assistant><script src="${process.env.REACT_APP_URL}/widgets/v1/ai-widget.js" async></script>`
                                                                            )
                                                                        })()}
                                                                    </pre>
                                                                </div>
                                                                <Button
                                                                    color="primary"
                                                                    size="sm"
                                                                    outline
                                                                    id={`copyCode`}
                                                                    onClick={() => copyEmbedCode(
                                                                        `
                                                                        <ai-assistant agent-id="${agent.agentId}" userId="${localStorage.getItem("userId")}" ${!agent.inheritFromWeb ? `backgroundColor="${agent.backgroundColor}" textColor="${agent.textColor}" chatTextColor="${agent.chatTextColor}" headerColor="${agent.headerColor}" buttonColor="${agent.buttonColor}" borderRadius="${agent.borderRadius}"` : ""}></ai-assistant>
                                                                        <script src="${process.env.REACT_APP_URL}/widgets/v1/ai-widget.js" async></script>
                                                                        `
                                                                    )}
                                                                    className="d-inline-flex align-items-center justify-content-end gap-1 me-2"
                                                                >
                                                                    <i className="ri-file-copy-line"></i>
                                                                </Button>
                                                                <UncontrolledTooltip placement="top" target={`copyCode`}>
                                                                    Copy Embed Code
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        </Col>
                                                        <Col>
                                                            <Button disabled={loading} onClick={() => { setShowChatAgent(true) }} className="px-4 py-2 rounded linear-gradient-btn">{loading ?
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                    Please Wait
                                                                </> : "Test Chat"}</Button>
                                                        </Col>

                                                    </Row>
                                                </CardBody>
                                                {
                                                    showChatAgent && <ChatComponent agentId={agent.agentId} userId={localStorage.getItem("userId")} backgroundColor={agent.backgroundColor} textColor={agent.textColor} chatTextColor={agent.chatTextColor} headerColor={agent.headerColor} buttonColor={agent.buttonColor} borderRadius={agent.borderRadius}></ChatComponent>
                                                }
                                            </Card>
                            }
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default UpdateAgent;